import type { GetStaticPaths, GetStaticProps } from 'next';
import type { ParsedUrlQuery } from 'querystring';

import { StaticSitePath } from '../static-sites/models';
import type { StaticPageProp } from '../static-sites/models';
import {
  STATIC_SITE_PROP_MAP,
  TEMPLATE_IMPORT_MAP,
  UN_SERIALIZABLE_PROP_MAP
} from '../static-sites/template-registry';

/**
 * Steps to create a new static site:
 * - Adding new path to @see {StaticSitePath}
 * - Creating the static props for the page and add it to @see {STATIC_SITE_PROP_MAP}
 * - Creating import function for specific template component -> @see {TEMPLATE_IMPORT_MAP}
 * - Adding the new path to @see {getStaticPaths} function to create it statically
 */

type StaticSiteProps = { staticSite?: StaticSitePath } & StaticPageProp;

const StaticSite = ({ staticSite, ...props }: StaticSiteProps) => {
  if (!staticSite) {
    return <></>;
  }
  const importComponent = TEMPLATE_IMPORT_MAP[staticSite];
  const extraProps = UN_SERIALIZABLE_PROP_MAP[staticSite];

  const Component = importComponent();

  return (
    <>
      {/* eslint-disable-next-line react-hooks/static-components */}
      <Component {...props} {...(extraProps || {})} />
    </>
  );
};

export default StaticSite;

interface Params extends ParsedUrlQuery {
  staticSite?: StaticSitePath;
}

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  const paths = [
    {
      params: {
        staticSite: StaticSitePath.EPD_ERROR
      }
    }
  ];

  const staticPaths = Object.values(StaticSitePath);

  const missingPaths = staticPaths.filter(
    (staticPath) => !paths.some((path) => path.params.staticSite === staticPath)
  );

  if (missingPaths.length > 0) {
    throw new Error(
      `The following static path values are missing in the provided 'paths' array: ${missingPaths.join(
        ', '
      )}`
    );
  }

  return {
    paths,
    fallback: false
  };
};

const isValidStaticSitePath = (
  staticSitePath: string | string[] | undefined
): staticSitePath is StaticSitePath =>
  typeof staticSitePath === 'string' && !!STATIC_SITE_PROP_MAP[staticSitePath as StaticSitePath];

export const getStaticProps: GetStaticProps<StaticSiteProps | { staticSite: undefined }> = async ({
  params
}) => {
  const { staticSite } = params || {};

  if (isValidStaticSitePath(staticSite)) {
    return {
      props: {
        staticSite,
        ...STATIC_SITE_PROP_MAP[staticSite]
      },
      revalidate: false
    };
  }
  return { props: { staticSite: undefined } };
};
